import * as actions from '@actions';
import { useDispatch } from 'react-redux';
import assert from 'assert';

type Dispatcher = ReturnType<typeof useDispatch>;
type FileContents = string | ArrayBuffer | null;
type S3FileData = { status: number; url: string | null; mime?: string };

// PUT "binary" to "url"
async function uploadBinaryToURL(url: string, mime: string, binary: FileContents) {
  return fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': mime,
      redirect: 'follow'
    },
    body: binary
  });
}

// upload "binary" data (for the given user survey) with the given filename and mimeType.
// Uploaded files are associated with a specific survey (surveyjs_id) and the DB file
// upload is recorded in the DB so that the file can be recorded in the survey "Documents".
export async function uploadBinaryToS3(
  binary: FileContents,
  dispatch: Dispatcher,
  user_guid: string,
  filename: string,
  surveyjs_id: number,
  mimeType: string,
  userHasReadPermissions: boolean
) {
  let base64 = '';
  try {
    // @ts-expect-error
    base64 = btoa(String.fromCharCode(...new Uint8Array(binary, 0, 4 * 1024)));
  } catch {
    console.error(`Converting to base 64 failed for file ${filename}, mime type ${mimeType}`);
  }

  if (base64 === '') {
    throw new Error('Failed to upload cropped image to server.');
  }

  // this is a misnomer ... this API generates an S3 pre-signed URL and returns it as url
  const tempUrlResponse = await dispatch(
    actions.uploadDocs({
      user_guid,
      filename,
      survey_id: surveyjs_id,
      base64,
      tempurl: true
    })
  );
  const {
    payload: { url, mime },
    type
  } = tempUrlResponse;

  if (!mime?.startsWith(mimeType)) {
    throw Error(`File upload failed with unexpected file type - expected "${mimeType}", got "${mime}" for ${filename}`);
  }

  if (type !== 'UPLOAD_DOCS_SUCCESS') {
    throw Error(`File upload failed requesting temporary URL for file ${filename}`);
  }

  assert(typeof url === 'string');
  assert(typeof mime === 'string');

  // now for the tricky bit ... post the data to the URL
  const uploadBinaryResponse = await uploadBinaryToURL(url, mime, binary).catch(() => {
    console.error(`File upload failed attempting to upload binary for file ${filename} to url ${url}`);
  });

  if (!uploadBinaryResponse?.ok) {
    throw Error(`File upload failed attempting to upload binary for file ${filename} to url ${url}`);
  }

  // this is a misnomer ... this API generates an S3 pre-signed URL and returns it as url
  const acknowledgeUploadResponse = await dispatch(
    actions.uploadDocs({
      user_guid,
      filename,
      survey_id: surveyjs_id,
      uploaded: true
    })
  );

  if (acknowledgeUploadResponse.type !== 'UPLOAD_DOCS_SUCCESS') {
    throw Error(`File upload failed when acknowledging upload for ${filename}`);
  }

  const refetchedDocResponse = (await dispatch(
    actions.getDocs({
      user_guid,
      filename,
      tempurl: true,
      uploaded: true
    }),
    // if the user *doesn't* have read permissions *don't* throw
    userHasReadPermissions
  )) as S3FileData;

  if (refetchedDocResponse.status !== 200 && userHasReadPermissions) {
    throw Error(`File upload failed when refetching file ${filename}`);
  }

  // TODO: @Alastair - remove mimeType after everything is TypeScripted up - I think it is just an innocent synonym
  return { ...refetchedDocResponse, mimeType: mime };
}

export const formatFileName = (file: File, surveyId: number, isCroppedImage: boolean) => {
  const originalFilename = file.name;
  const adjustedFilename = `${originalFilename.split('.')[0]}_${file.lastModified}.${originalFilename.split('.').pop()}`;
  return `${isCroppedImage ? 'cropped_' : ''}${surveyId}${adjustedFilename}`;
};

// A wrapper around uploadBinaryToS3 taking the uploaded data from a browser File instance
export const uploadFileToS3 = async (
  file: File,
  dispatch: Dispatcher,
  user_guid: string,
  userHasReadPermissions: boolean,
  surveyjs_id: number,
  mimeType = '',
  isCroppedImage = false
) => {
  const loadFileAsBinary = (file: File) =>
    new Promise<FileContents>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => {
        console.error(`Error loading file ${file?.name} as binary`, error);
        return reject(error);
      };
      reader.readAsArrayBuffer(file);
    });
  // Append ${_lastModified}_ to file name for uniqueness
  const finalFilename = formatFileName(file, surveyjs_id, isCroppedImage);
  console.log(`Updated file name from ${file?.name} to ${finalFilename}`);
  const binary = await loadFileAsBinary(file);

  console.log(`Uploading binary for ${finalFilename}...`);
  const result = await uploadBinaryToS3(
    binary,
    dispatch,
    user_guid,
    finalFilename,
    surveyjs_id,
    mimeType,
    userHasReadPermissions
  );

  return { ...result, file: new File([file], finalFilename, { type: file.type }) };
};

export default uploadFileToS3;
